import { denormalizeAssetData } from '../util/data';
import { storableError } from '../util/errors';
import * as log from '../util/log';

// Pick paths from entries of appCdnAssets config (in configDefault.js)
const pickHostedConfigPaths = (assetEntries, excludeAssetNames) => {
  // E.g. allPaths = ['any/foo.json', 'any/bar.json']
  return assetEntries.reduce((pickedPaths, [name, path]) => {
    if (excludeAssetNames.includes(name)) {
      return pickedPaths;
    }
    return [...pickedPaths, path];
  }, []);
};
const getFirstAssetData = response => response?.data?.data[0]?.attributes?.data;
const getMultiAssetData = response => response?.data?.data;
const getMultiAssetIncluded = response => response?.data?.included;
const findJSONAsset = (assets, absolutePath) =>
  assets.find(a => a.type === 'jsonAsset' && a.attributes.assetPath === absolutePath);
const getAbsolutePath = path => (path.charAt(0) !== '/' ? `/${path}` : path);

const getGoogleAnalyticsId = (configAssets, path) => {
  if (!configAssets || !path) {
    return null;
  }
  const configAssetsData = getMultiAssetData(configAssets);
  const jsonAsset = findJSONAsset(configAssetsData, getAbsolutePath(path));
  const { enabled, measurementId } = jsonAsset?.attributes?.data?.googleAnalytics || {};
  return enabled ? measurementId : null;
};

const isHostedAsset404 = err =>
  err?.status === 404 ||
  err?.response?.status === 404 ||
  (Array.isArray(err?.apiErrors) && err.apiErrors[0]?.status === 404);

// ================ Action types ================ //

export const ASSETS_REQUEST = 'app/assets/REQUEST';
export const ASSETS_SUCCESS = 'app/assets/SUCCESS';
export const ASSETS_ERROR = 'app/assets/ERROR';

export const PAGE_ASSETS_REQUEST = 'app/assets/PAGE_ASSETS_REQUEST';
export const PAGE_ASSETS_SUCCESS = 'app/assets/PAGE_ASSETS_SUCCESS';
export const PAGE_ASSETS_ERROR = 'app/assets/PAGE_ASSETS_ERROR';

// ================ Reducer ================ //

const initialState = {
  // List of app-wide assets that should be fetched and their path in Asset API.
  // appAssets: { assetName: 'path/to/asset.json' }
  appAssets: {},
  pageAssetsData: null,
  currentPageAssets: [],
  // Current version of the saved asset.
  // Typically, the version that is returned by the "latest" alias.
  version: null,
  inProgress: false,
  error: null,
};

export default function assetReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case ASSETS_REQUEST:
      return { ...state, inProgress: true, error: null };
    case ASSETS_SUCCESS:
      return {
        ...state,
        appAssets: payload.assets,
        version: payload.version != null ? payload.version : state.version,
        googleAnalyticsId: payload.googleAnalyticsId,
        inProgress: false,
      };
    case ASSETS_ERROR:
      return { ...state, inProgress: false, error: payload };

    case PAGE_ASSETS_REQUEST:
      return { ...state, currentPageAssets: payload, inProgress: true, error: null };
    case PAGE_ASSETS_SUCCESS:
      return { ...state, pageAssetsData: payload, inProgress: false };
    case PAGE_ASSETS_ERROR:
      return { ...state, inProgress: false, error: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const appAssetsRequested = () => ({ type: ASSETS_REQUEST });
export const appAssetsSuccess = (assets, version, googleAnalyticsId) => ({
  type: ASSETS_SUCCESS,
  payload: { assets, version, googleAnalyticsId },
});
export const appAssetsError = error => ({
  type: ASSETS_ERROR,
  payload: error,
});

export const pageAssetsRequested = assetKeys => ({ type: PAGE_ASSETS_REQUEST, payload: assetKeys });
export const pageAssetsSuccess = assets => ({ type: PAGE_ASSETS_SUCCESS, payload: assets });
export const pageAssetsError = error => ({
  type: PAGE_ASSETS_ERROR,
  payload: error,
});

// ================ Thunks ================ //

export const fetchAppAssets = (assets, version) => (dispatch, getState, sdk) => {
  dispatch(appAssetsRequested());

  const translationsPath = assets.translations;
  const footerPath = assets.footer;

  const assetEntries = Object.entries(assets);
  const nonConfigAssets = ['translations', 'footer'];
  const configPaths = pickHostedConfigPaths(assetEntries, nonConfigAssets);

  const loadForVersionHash = versionHash => {
    const fetchAssets = paths =>
      versionHash
        ? sdk.assetsByVersion({ paths, version: versionHash })
        : sdk.assetsByAlias({ paths, alias: 'latest' });

    const separateAssetFetches = [
      fetchAssets([translationsPath]),
      fetchAssets([footerPath]),
      fetchAssets(configPaths),
    ];

    return Promise.all(separateAssetFetches).then(([translationAsset, footerAsset, configAssets]) => {
      const getVersionHash = response => response?.data?.meta?.version;
      const versionInTranslationsCall = getVersionHash(translationAsset);
      const versionInFooterCall = getVersionHash(footerAsset);
      const versionInConfigsCall = getVersionHash(configAssets);
      const hasSameVersions =
        versionInTranslationsCall === versionInFooterCall &&
        versionInFooterCall === versionInConfigsCall;

      if (!versionHash && !hasSameVersions) {
        console.warn("Asset versions between calls don't match.");
      }

      const googleAnalyticsId = getGoogleAnalyticsId(configAssets, assets.analytics);
      dispatch(appAssetsSuccess(assets, versionInTranslationsCall, googleAnalyticsId));

      return assetEntries.reduce((collectedAssets, assetEntry) => {
        const [name, path] = assetEntry;

        if (nonConfigAssets.includes(name)) {
          const assetResponse = name === 'translations' ? translationAsset : footerAsset;
          return { ...collectedAssets, [name]: { path, data: getFirstAssetData(assetResponse) } };
        }

        const fetchedConfigAssets = getMultiAssetData(configAssets);
        const jsonAsset = findJSONAsset(fetchedConfigAssets, getAbsolutePath(path));

        const data = denormalizeAssetData({
          data: jsonAsset?.attributes?.data,
          included: getMultiAssetIncluded(configAssets),
        });
        return { ...collectedAssets, [name]: { path, data } };
      }, {});
    });
  };

  const requestedVersion = version || null;

  return loadForVersionHash(requestedVersion).catch(e => {
    if (requestedVersion && isHostedAsset404(e)) {
      console.warn('app-asset-fetch-retry-with-latest-alias', {
        failedVersion: requestedVersion,
        message: e?.message,
      });
      return loadForVersionHash(null).catch(e2 => {
        log.error(e2, 'app-asset-fetch-failed', { assets, version: requestedVersion, retriedLatest: true });
        dispatch(appAssetsError(storableError(e2)));
      });
    }
    log.error(e, 'app-asset-fetch-failed', { assets, version: requestedVersion });
    dispatch(appAssetsError(storableError(e)));
  });
};

export const fetchPageAssets = (assets, hasFallback) => (dispatch, getState, sdk) => {
  const reduxVersion = getState()?.hostedAssets?.version;
  if (typeof reduxVersion === 'undefined') {
    throw new Error(
      'App-wide assets were not fetched first. Asset version missing from Redux store.'
    );
  }

  dispatch(pageAssetsRequested(Object.keys(assets)));

  const assetEntries = Object.entries(assets);

  const loadPageAssetsForVersion = versionHash => {
    const fetchOne = assetPath =>
      versionHash
        ? sdk.assetByVersion({ path: assetPath, version: versionHash })
        : sdk.assetByAlias({ path: assetPath, alias: 'latest' });

    const sdkAssets = assetEntries.map(([, assetPath]) => fetchOne(assetPath));

    return Promise.all(sdkAssets).then(responses => {
      const hostedAssetsState = getState()?.hostedAssets;
      const { termsOfService, privacyPolicy, landingPage, ...rest } =
        hostedAssetsState?.pageAssetsData || {};
      const fixedPageAssets = { termsOfService, privacyPolicy, landingPage };
      const pickLatestPageAssetData = hostedAssetsState?.currentPageAssets.reduce(
        (collected, pa) => {
          const cmsPageData = rest[pa];
          return cmsPageData ? { ...collected, [pa]: cmsPageData } : collected;
        },
        {}
      );
      const pageAssets = assetEntries.reduce(
        (collectedAssets, assetEntry, i) => {
          const [name, path] = assetEntry;
          const assetData = denormalizeAssetData(responses[i].data);
          return { ...collectedAssets, [name]: { path, data: assetData } };
        },
        { ...fixedPageAssets, ...pickLatestPageAssetData }
      );
      dispatch(pageAssetsSuccess(pageAssets));
      return pageAssets;
    });
  };

  const versionHash = reduxVersion || null;

  return loadPageAssetsForVersion(versionHash).catch(e => {
    if (versionHash && isHostedAsset404(e)) {
      console.warn('page-asset-fetch-retry-with-latest-alias', {
        failedVersion: versionHash,
        message: e?.message,
      });
      return loadPageAssetsForVersion(null).catch(e2 => {
        if (hasFallback || isHostedAsset404(e2)) {
          log.error(e2, 'page-asset-fetch-failed', {
            assets,
            version: reduxVersion,
            retriedLatest: true,
          });
        }
        dispatch(pageAssetsError(storableError(e2)));
      });
    }
    if (hasFallback || isHostedAsset404(e)) {
      log.error(e, 'page-asset-fetch-failed', { assets, version: reduxVersion });
    }
    dispatch(pageAssetsError(storableError(e)));
  });
};
